import { hasMarkupAboveTotal, isPrice } from './finalPricing';

// Hoisted so a comparator reading them allocates nothing per comparison.
const TOTAL_PATH = ['pricing', 'total'];
const TOTAL_WITH_MARKUP_PATH = ['pricing', 'totalWithMarkup'];

/**
 * The BOOK-168 twin of `displayedPriceFromTrip`: the final-arm sort figure of an Immutable trip,
 * for `utils/tripFilters`, the one surface that sorts Immutable maps.
 *
 * It reads `pricing`, the key control sorts by, so the final arm swaps the figure and nothing else
 * (KD7): `totalWithMarkup`, or `total` when the companion is missing or not a number, the same
 * `isPrice` screen `finalPrice` applies, so a trip sorts by the figure its card paints. A companion
 * of 0 is a figure, as with INS-1288's display total. A trip without a companion -- no markup rule
 * on its route -- falls back to its base total, which is what its card paints.
 *
 * @param {Immutable.Map} trip - A trip with a `pricing` map.
 * @param {boolean} [isFinal] - Whether this user is in the final arm.
 * @returns {number|undefined} The figure the trip is sorted by.
 */
export function finalPriceFromTrip(trip, isFinal = false) {
  if (!isFinal) return trip?.getIn?.(TOTAL_PATH);

  // The companion first: the comparator runs on every pair, and the base total is only its
  // fallback.
  const companion = trip?.getIn?.(TOTAL_WITH_MARKUP_PATH);
  return isPrice(companion) ? companion : trip?.getIn?.(TOTAL_PATH);
}

/**
 * `hasMarkupAboveTotal` over an Immutable trip's `pricing`.
 *
 * @param {Immutable.Map} trip - A trip with a `pricing` map.
 * @returns {boolean} Whether its companion is above its total.
 */
const tripHasMarkupAboveTotal = (trip) => {
  const totalWithMarkup = trip?.getIn?.(TOTAL_WITH_MARKUP_PATH);
  // No companion, no markup: the case of every trip for tenants without the backend flag, so the
  // scan skips the second read and the object the rule takes.
  if (totalWithMarkup == null) return false;

  return hasMarkupAboveTotal({ total: trip.getIn(TOTAL_PATH), totalWithMarkup });
};

/**
 * Whether any trip in an Immutable list has a companion above its total, i.e. whether the final
 * arm would order the list differently at all. The list-level gate of the price sort, the
 * counterpart of `someTripHasDisplayTotal`; it applies `hasMarkupAboveTotal` itself, so the sort
 * and the cards gate on one spelling of the rule.
 *
 * @param {Immutable.List} trips - The filtered trips about to be sorted.
 * @returns {boolean} Whether at least one trip has a positive markup.
 */
export function someTripHasMarkupAboveTotal(trips) {
  return Boolean(trips?.some?.(tripHasMarkupAboveTotal));
}
